import React from "react";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Paper,
  Stack,
  Divider,
  List,       // Using List for structured items
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,     // For icons
  Alert,      // For prominent status
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FolderZipIcon from '@mui/icons-material/FolderZip'; // Example Icon
import ArticleIcon from '@mui/icons-material/Article';    // Example Icon
import SubjectIcon from '@mui/icons-material/Subject';      // Example Icon
import AccountTreeIcon from '@mui/icons-material/AccountTree'; // Example Icon
import BusinessIcon from '@mui/icons-material/Business';    // Example Icon
import LabelIcon from '@mui/icons-material/Label';        // Example Icon
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'; // Status Icons
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { styled, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

// --- Styled Components ---

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(1.5, 2), // Slightly more padding
  justifyContent: 'space-between', // Space between title and close button
  backgroundColor: theme.palette.primary.main, // Use primary color for header
  color: theme.palette.primary.contrastText, // Ensure text is readable
  flexShrink: 0, // Prevent header from shrinking
}));

const SectionPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5, // Slightly more rounded corners
  boxShadow: theme.shadows[2], // Subtle shadow
  backgroundColor: theme.palette.background.paper, // Default paper background
}));

const DetailListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1, 0), // Adjust padding for list items
  alignItems: 'flex-start', // Align items top for multi-line text
}));

const DetailLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.text.secondary,
  fontSize: '0.8rem', // Smaller label
  marginBottom: theme.spacing(0.25), // Small space below label
  display: 'block',
}));

const DetailValue = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  color: theme.palette.text.primary,
  fontSize: '0.95rem', // Slightly larger value text
  wordBreak: 'break-word',
}));

// --- Helper Function for Status ---

const getStatusProps = (status: string | undefined, theme: any) => {
  const lowerStatus = status?.toLowerCase() || "unknown";

  if (lowerStatus.includes("complete") || lowerStatus.includes("approved")) {
    return {
        severity: "success" as const,
        icon: <CheckCircleOutlineIcon fontSize="inherit" />,
        label: status || "Completed"
    };
  } else if (lowerStatus.includes("pending") || lowerStatus.includes("review")) {
    return {
        severity: "warning" as const,
        icon: <PendingActionsIcon fontSize="inherit" />,
        label: status || "Pending"
    };
  } else if (lowerStatus.includes("reject") || lowerStatus.includes("closed")) {
    return {
        severity: "error" as const,
        icon: <ErrorOutlineIcon fontSize="inherit" />,
        label: status || "Rejected"
    };
  } else {
    return {
        severity: "info" as const,
        icon: <InfoOutlinedIcon fontSize="inherit" />,
        label: status || "Unknown"
     };
  }
};

// --- Component ---

interface Props {
  open: boolean;
  onClose: () => void;
  userData: any; // Define a more specific type if possible
  title: string;
}

function DocMngSwipeableDrawer({ open, onClose, title, userData }: Props) {
  const { t } = useTranslation();
  const theme = useTheme();

  const handleClose = () => {
    onClose();
  };

  const isValidUserData = userData && typeof userData === 'object' && !Array.isArray(userData);
  const statusProps = isValidUserData ? getStatusProps(userData.complt, theme) : getStatusProps(undefined, theme);

  return (
    <SwipeableDrawer
      anchor="right"
      open={open}
      onClose={handleClose}
      onOpen={() => {}}
      disableBackdropTransition
      slotProps={{
        backdrop: {
          style: { backgroundColor: "rgba(0, 0, 0, 0.65)" }, // Slightly darker backdrop
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: '95vw', sm: '70vw', md: '550px', lg: '600px' }, // Generous width
          boxShadow: theme.shadows[12],
          backgroundColor: theme.palette.grey[100], // Light grey background for the whole drawer
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
      style={{ zIndex: 1300 }}
    >
      {/* Header */}
      <DrawerHeader>
        <Typography
          variant="h6"
          component="div"
          sx={{ fontWeight: 600, flexGrow: 1, mr: 1 }} // Title grows
        >
          {title}
        </Typography>
        <IconButton
          aria-label={t('common.close') || 'Close'} // Add translation for close button
          onClick={handleClose}
          sx={{ color: theme.palette.primary.contrastText }} // Ensure contrast
        >
          <CloseIcon />
        </IconButton>
      </DrawerHeader>

      {/* Content Area */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: { xs: 2, sm: 3 } }}>
        {isValidUserData ? (
          <>
           
            <SectionPaper>
              <List dense disablePadding> {/* Dense list for compactness */}
                <DetailListItem>
                  <ListItemAvatar sx={{ minWidth: 45}}>
                    <Avatar sx={{ bgcolor: theme.palette.info.light }}>
                      <ArticleIcon fontSize="small" sx={{ color: theme.palette.info.contrastText }}/>
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<DetailLabel>{t("text.FileNo")}</DetailLabel>}
                    secondary={<DetailValue sx={{ fontWeight: 600, fontSize: '1rem' }}>{userData.fileNo || '-'}</DetailValue>} // More prominent File No
                  />
                </DetailListItem>
                <Divider variant="inset" component="li" sx={{ ml: '57px !important' }} />
                <DetailListItem>
                  <ListItemAvatar sx={{ minWidth: 45 }}>
                    <Avatar sx={{ bgcolor: theme.palette.secondary.light }}>
                      <LabelIcon fontSize="small" sx={{ color: theme.palette.secondary.contrastText }}/>
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<DetailLabel>{t("text.FileName")}</DetailLabel>}
                    secondary={<DetailValue>{userData.synopsis || '-'}</DetailValue>}
                  />
                </DetailListItem>
              </List>
            </SectionPaper>

            {/* Subject & Type Info */}
            <SectionPaper>
               <List dense disablePadding>
                 <DetailListItem>
                  <ListItemAvatar sx={{ minWidth: 45 }}>
                    <Avatar sx={{ bgcolor: theme.palette.grey[300] }}>
                      <SubjectIcon fontSize="small" color="action"/>
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<DetailLabel>{t("text.Subject")}</DetailLabel>}
                    secondary={<DetailValue>{userData.sub_Subject_Name || '-'}</DetailValue>}
                  />
                </DetailListItem>
                 <Divider variant="inset" component="li" sx={{ ml: '57px !important' }}/>
                <DetailListItem>
                   <ListItemAvatar sx={{ minWidth: 45 }}>
                    <Avatar sx={{ bgcolor: theme.palette.grey[300] }}>
                      <AccountTreeIcon fontSize="small" color="action" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<DetailLabel>{t("text.SubSubjectName")}</DetailLabel>}
                    secondary={<DetailValue>{userData.sub_Subject_Name || '-'}</DetailValue>}
                  />
                </DetailListItem>
                 <Divider variant="inset" component="li" sx={{ ml: '57px !important' }}/>
                 <DetailListItem>
                   <ListItemAvatar sx={{ minWidth: 45 }}>
                    <Avatar sx={{ bgcolor: theme.palette.grey[300] }}>
                      <FolderZipIcon fontSize="small" color="action" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<DetailLabel>{t("text.FileType")}</DetailLabel>}
                    secondary={<DetailValue>{userData.fileType_Name || '-'}</DetailValue>}
                  />
                </DetailListItem>
               </List>
            </SectionPaper>

            {/* Context Info */}
            <SectionPaper>
               <List dense disablePadding>
                 <DetailListItem>
                   <ListItemAvatar sx={{ minWidth: 45 }}>
                    <Avatar sx={{ bgcolor: theme.palette.grey[300] }}>
                      <BusinessIcon fontSize="small" color="action"/>
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<DetailLabel>{t("text.DivisionName")}</DetailLabel>}
                    secondary={<DetailValue>{userData.department_Name || '-'}</DetailValue>}
                  />
                </DetailListItem>
                {/* Add more details here if needed following the pattern */}
               </List>
            </SectionPaper>

          </>
        ) : (
          <Alert severity="warning" sx={{ m: 2 }}>
            {t("text.NoDetailsAvailable", "No details available to display.")}
          </Alert>
        )}
      </Box>
    </SwipeableDrawer>
  );
}

export default DocMngSwipeableDrawer; // Renamed component for clarity